import { useEffect, useState } from 'react';

interface UseTargetHeightOptions {
	/**
	 * The ID of the element that this sticky element should stack below.
	 * If not provided, this element will be the top-level sticky element.
	 */
	elementId?: string;
}

export function useTargetHeight(options: UseTargetHeightOptions = {}) {
	const { elementId } = options;
	const [topHeight, setTopHeight] = useState(0);

	useEffect(() => {
		if (!elementId) {
			setTopHeight(0);
			return;
		}
		const targetElement = document.getElementById(elementId);
		if (!targetElement) {
			setTopHeight(0);
			return;
		}

		const updateHeight = () => {
			const targetHeight = targetElement.offsetHeight;
			setTopHeight(targetHeight);
		};

		// Create intersection observer to watch the target element
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach(() => {
					updateHeight();
				});
			},
			{
				threshold: [0, 1],
				rootMargin: '0px',
			}
		);

		observer.observe(targetElement);

		window.addEventListener('resize', updateHeight);
		// Initial position calculation
		updateHeight();

		return () => {
			observer.disconnect();
			window.removeEventListener('resize', updateHeight);
		};
	}, [elementId]);

	return topHeight;
}
